import { Checkbox, ControlLabel, FormControl, FormGroup, Modal } from 'react-bootstrap';
import React, { ChangeEvent, FormEventHandler, useCallback, useState } from 'react';
import ClassyButton from '../ClassyButton/ClassyButton';
import { SimpleValidationResult } from '../../Helpers/validators';
import ValidationMessage, { getValidationState } from './ValidationMessage';
import { useSelector } from 'react-redux';
import Throbber from '../Throbber/Throbber';
import { useIsClassyAdmin } from './RlcHooks';

interface AddCampaignProps {
  onSave: (addRlcCampaign: AddRlcCampaignForm) => void;
  onCancel: () => void;
  error: string | null;
  isInFlight: boolean;
}

export interface AddRlcCampaignForm {
  classyCampaignId: number;
  usesZipCode: boolean;
  usesActiveDesignations: boolean;
}

const AddCampaign = ({ onSave, onCancel, error, isInFlight }: AddCampaignProps) => {
  const [classyCampaignId, setClassyCampaignId] = useState<string>('');
  const selectedClassyOrganizationId = useSelector<any, number | undefined>(
    (state) => state.login?.selectedOrganization?.classyOrganizationId,
  );
  const [usesZipCode, setUsesZipCode] = useState<boolean>(true);
  const [usesActiveDesignations, setUsesActiveDesignations] = useState<boolean>(false);
  const isClassyAdmin = useIsClassyAdmin();

  const [validationError] = useState<SimpleValidationResult>({});

  const onInputChange: FormEventHandler<FormControl> = useCallback((event) => {
    // not sure why the callback isn't for ChangeEvent
    const e = event as unknown as ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
    const value = e?.target?.value || '';
    setClassyCampaignId(value.replace(/\D/g, ''));
  }, []);

  const onSaveRequested = useCallback(() => {
    if (classyCampaignId) {
      onSave({
        classyCampaignId: Number(classyCampaignId),
        usesZipCode,
        usesActiveDesignations,
      });
    } else {
      console.error('no orgid selected');
    }
  }, [usesZipCode, usesActiveDesignations, classyCampaignId, onSave]);

  const onCheckedChangedZipCode: FormEventHandler<FormControl> = useCallback((event) => {
    const e = event as unknown as ChangeEvent<HTMLInputElement>;
    const value = e?.target?.checked;
    setUsesZipCode(value);
  }, []);

  const onCheckedChangedAddDesignations: FormEventHandler<FormControl> = useCallback((event) => {
    const e = event as unknown as ChangeEvent<HTMLInputElement>;
    const value = e?.target?.checked;
    setUsesActiveDesignations(value);
  }, []);

  return (
    <>
      {error && <div className="rlcDesignations__error_text">{error}</div>}
      <FormGroup
        controlId="classyCampaignId"
        validationState={getValidationState(validationError, 'classyOrganizationId')}
      >
        <ControlLabel>
          Classy{' '}
          <a
            rel="noreferrer"
            href={`${process.env.REACT_APP_CLASSY_CORE_URL}/admin/${selectedClassyOrganizationId}/campaigns`}
            target="_blank"
          >
            Campaign Id
          </a>
        </ControlLabel>
        <FormControl
          name="classyCampaignId"
          type="text"
          value={classyCampaignId}
          placeholder="Enter a classy campaign id"
          onChange={onInputChange}
          style={{ width: '50%' }}
        />
        <ValidationMessage field="classyCampaignId" validationError={validationError} />
      </FormGroup>

      {isClassyAdmin ? (
        <>
          <Checkbox
            data-testid="classy-admin-rlc-zip-code-tag-checkbox"
            checked={usesZipCode}
            onChange={onCheckedChangedZipCode}
            validationState={getValidationState(validationError, 'usesZipCode') || undefined}
          >
            Automatically assign designations based on zip code unless donor chooses a designation.
          </Checkbox>
          <Checkbox
            data-testid="classy-admin-rlc-add-all-active-designations-checkbox"
            checked={usesActiveDesignations}
            onChange={onCheckedChangedAddDesignations}
            validationState={getValidationState(validationError, 'usesActiveDesignations') || undefined}
          >
            Enable zip code override and add all active designations to the campaign.
          </Checkbox>
        </>
      ) : (
        <Checkbox
          data-testid="org-admin-rlc-zip-code-tag-checkbox"
          checked={usesZipCode}
          disabled={true}
          onChange={onCheckedChangedZipCode}
          validationState={getValidationState(validationError, 'usesZipCode') || undefined}
        >
          Automatically assign designations based on zip code.
        </Checkbox>
      )}

      {isInFlight && (
        <div className="rlcDesignations__throbber_container">
          <Throbber loading={isInFlight} />
        </div>
      )}
      {!isInFlight && (
        <div className="submit-button text-center" style={{ marginTop: 20 }}>
          <ClassyButton title="Cancel" className={'secondary-button'} onClick={onCancel} />
          <ClassyButton title="Add" disabled={!classyCampaignId} onClick={onSaveRequested} />
        </div>
      )}
    </>
  );
};

interface AddCampaignModalProps {
  isOpen: boolean;
  isInFlight: boolean;
  onCancel: () => void;
  onSave: (addCampaign: AddRlcCampaignForm) => void;
  title: string;
  error: string | null;
}

// SEE: https://react-bootstrap-v3.netlify.app/components/modal/
const AddCampaignModal = ({ isOpen, title, ...props }: AddCampaignModalProps) => (
  <Modal show={isOpen} onHide={props.onCancel} bsSize="large">
    <Modal.Header closeButton>
      <Modal.Title>{title}</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <AddCampaign {...props} />
    </Modal.Body>
  </Modal>
);

export default AddCampaignModal;
